/**
 * UnifiedSidebar - Two-tab sidebar: "Chats" | "Documentos"
 *
 * Tab "Chats": conversations list + mini "docs en contexto" footer
 * Tab "Documentos": 4 doc sections + "Gestionar biblioteca →"
 *
 * Desktop: w-72/w-0 with transition (lateral collapse).
 * Close handled by PanelLeft toggle in ChatPanelHeader.
 * Mobile: fullscreen via mobileFullscreen prop (managed by MainLayout).
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Plus,
  Search,
  Check,
  Clock,
  Globe,
  User,
  Upload,
  HardDrive,
  FileQuestion,
  Compass,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useDocumentSelector } from '../hooks/useDocumentSelector';
import ConversationDrawer from './ConversationDrawer';
import SectionHeader from './sidebar/SectionHeader';
import BibliotecaExplorer from './sidebar/BibliotecaExplorer';
import DocTooltip, { useDocTooltip } from './sidebar/DocTooltip';
import type { DocTooltipDoc } from './sidebar/DocTooltip';
import DocSection from './chat/document-panel/AllDocsTab';
import { apiService } from '../services/api';
import SelectedDocsTab from './chat/document-panel/SelectedDocsTab';
import type { Conversation } from '../services/api';
import type { ConversationUpdateRequest } from '../services/api/types/conversation';
import type { MinimalDocument } from '../types';
import type { SelectionSource } from '../types/documentSelection';
import type { DocumentSuggestion } from './chat/hooks/useDocumentSuggestions';
import { EventNames, useEventListener } from '../utils/eventBus';

// ─── localStorage helpers ─────────────────────────────────────────
const COLLAPSED_KEY = 'biblioteca-collapsed';
const TAB_KEY = 'sidebar-active-tab';

type SidebarTab = 'chats' | 'documentos';

const DOC_SECTIONS = new Set(['seleccionados', 'recientes', 'publica', 'personal', 'explorar']);

// Accordion group: only one of these can be expanded at a time (the 3 "aisles").
// Seleccionados (the "cart") is NOT in this group — always visible when it has items.
const ACCORDION_SECTIONS = new Set(['publica', 'personal', 'recientes', 'explorar']);

const getDefaultCollapsed = (): Set<string> => {
  try {
    const saved = localStorage.getItem(COLLAPSED_KEY);
    if (saved) {
      const parsed = JSON.parse(saved) as string[];
      if (Array.isArray(parsed)) {
        const migrated = parsed.map((s) => (s === 'sugeridos' ? 'seleccionados' : s));
        const result = new Set(migrated);
        // Accordion: ensure only one aisle is expanded
        const expandedAisles = [...ACCORDION_SECTIONS].filter((s) => !result.has(s));
        if (expandedAisles.length > 1) {
          // Keep only the first expanded, collapse the rest
          for (let i = 1; i < expandedAisles.length; i++) {
            result.add(expandedAisles[i]);
          }
        }
        return result;
      }
    }
  } catch {
    /* ignore */
  }
  // Default: publica expanded, personal + recientes + explorar collapsed
  return new Set(['personal', 'recientes', 'explorar']);
};

const getSavedTab = (): SidebarTab => {
  try {
    const saved = localStorage.getItem(TAB_KEY) as SidebarTab;
    if (saved === 'chats' || saved === 'documentos') return saved;
  } catch {
    /* ignore */
  }
  // Mobile defaults to 'chats' so users see chat, not 14K docs
  if (typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches) {
    return 'chats';
  }
  return 'documentos';
};

// ─── Props ────────────────────────────────────────────────────────
export interface UnifiedSidebarProps {
  isOpen: boolean;
  onClose: () => void;

  // Section expansion (from OPEN_DOCUMENT_PANEL event)
  expandSection?: string | null;
  onExpandSectionHandled?: () => void;

  // Navigation
  onToggleDrive?: () => void;

  // Conversation pass-through
  conversations: Conversation[];
  currentConversation: Conversation | null;
  onConversationSelect: (conv: Conversation) => void;
  onConversationCreate: (name: string) => Promise<any>;
  onConversationUpdate: (id: string, updates: ConversationUpdateRequest) => Promise<boolean>;

  // Document pass-through
  selectedDocumentIds: string[];
  selectedDocuments: MinimalDocument[];
  sources: Map<string, SelectionSource>;
  onDocumentsSelect: (ids: string[]) => void;
  suggestions: DocumentSuggestion[];
  isSuggestionsLoading: boolean;
  userPromptNeeded: boolean;
  documentSelectionMode: 'manual' | 'smart';

  // Upload
  onUploadPrivateFile?: (file: File) => Promise<void>;

  // Tab sync (for MobileBottomNav active state)
  onTabChange?: (tab: 'chats' | 'documentos') => void;

  // Mobile fullscreen mode (replaces overlay)
  mobileFullscreen?: boolean;

  // Hover peek: close sidebar when mouse leaves, pin on click
  onMouseLeave?: () => void;
  onInteraction?: () => void;
}

// ─── Component ────────────────────────────────────────────────────
const UnifiedSidebar: React.FC<UnifiedSidebarProps> = ({
  isOpen,
  onClose,
  expandSection,
  onExpandSectionHandled,
  onToggleDrive,
  // Conversation props
  conversations,
  currentConversation,
  onConversationSelect,
  onConversationCreate,
  onConversationUpdate,
  // Document props
  selectedDocumentIds,
  selectedDocuments,
  sources,
  onDocumentsSelect,
  suggestions,
  isSuggestionsLoading,
  userPromptNeeded,
  documentSelectionMode,
  onUploadPrivateFile,
  onTabChange,
  mobileFullscreen,
  onMouseLeave,
  onInteraction,
}) => {
  const { theme } = useTheme();

  // ─── Tab state ──────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<SidebarTab>(getSavedTab);

  const switchTab = useCallback(
    (tab: SidebarTab) => {
      setActiveTab(tab);
      setShellSearchTerm('');
      onTabChange?.(tab);
      try {
        localStorage.setItem(TAB_KEY, tab);
      } catch {
        /* ignore */
      }
    },
    [onTabChange]
  );

  // Sync initial tab to parent on mount
  useEffect(() => {
    onTabChange?.(activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Per-section scroll refs for virtualized lists (Pública/Personal)
  const publicaScrollRef = useRef<HTMLDivElement>(null);
  const personalScrollRef = useRef<HTMLDivElement>(null);

  // ─── Collapsed sections state ─────────────────────────────────
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(getDefaultCollapsed);

  const toggleCollapse = useCallback((section: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) {
        // Expanding — if it's an accordion aisle, collapse the others
        next.delete(section);
        if (ACCORDION_SECTIONS.has(section)) {
          for (const aisle of ACCORDION_SECTIONS) {
            if (aisle !== section) next.add(aisle);
          }
        }
      } else {
        next.add(section);
      }
      return next;
    });
  }, []);

  // Persist collapsed state
  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_KEY, JSON.stringify([...collapsedSections]));
    } catch {
      /* ignore */
    }
  }, [collapsedSections]);

  // ─── Unified search ────────────────────────────────────────────
  const [shellSearchTerm, setShellSearchTerm] = useState('');

  // ─── Conversation search (backend, searches message content) ──
  const [searchedConversations, setSearchedConversations] = useState<Conversation[] | null>(null);
  const [isSearchingChats, setIsSearchingChats] = useState(false);
  const chatSearchAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    // Only search backend when on Chats tab with ≥3 chars
    if (activeTab !== 'chats' || shellSearchTerm.trim().length < 3) {
      setSearchedConversations(null);
      return;
    }

    setIsSearchingChats(true);
    const timer = setTimeout(async () => {
      if (chatSearchAbortRef.current) chatSearchAbortRef.current.abort();
      chatSearchAbortRef.current = new AbortController();

      try {
        const result = await apiService.searchConversations(shellSearchTerm, true);
        if (result.conversations) {
          setSearchedConversations(result.conversations);
        }
      } catch {
        // Ignore abort errors
      } finally {
        setIsSearchingChats(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [shellSearchTerm, activeTab]);

  // ─── Document hook instances ───────────────────────────────────
  const sharedHook = useDocumentSelector({
    initialFilters: {},
    initialSearch: shellSearchTerm,
    selectedDocumentIds,
    onDocumentsSelect,
    enableLogging: false,
    enabled: true,
    overrideScope: 'shared',
  });

  const privateHook = useDocumentSelector({
    initialFilters: {},
    initialSearch: shellSearchTerm,
    selectedDocumentIds,
    onDocumentsSelect,
    enableLogging: false,
    enabled: true,
    overrideScope: 'private',
  });

  // Sync search to both hooks
  const handleSearchChange = useCallback(
    (term: string) => {
      setShellSearchTerm(term);
      if (activeTab === 'documentos') {
        sharedHook.setSearchTerm(term);
        privateHook.setSearchTerm(term);
      }
    },
    [activeTab, sharedHook.setSearchTerm, privateHook.setSearchTerm]
  );

  const recentDocs = sharedHook.recentDocs;

  // ─── Split shared docs into classified vs unclassified ────────
  const { classifiedDocs, unclassifiedDocs } = useMemo(() => {
    const classified: typeof sharedHook.documents = [];
    const unclassified: typeof sharedHook.documents = [];
    for (const doc of sharedHook.documents) {
      if (doc.company_name && doc.document_type) {
        classified.push(doc);
      } else {
        unclassified.push(doc);
      }
    }
    return { classifiedDocs: classified, unclassifiedDocs: unclassified };
  }, [sharedHook.documents]);

  // "Sin clasificar" sub-section — collapsed by default, persisted separately
  const [sinClasificarCollapsed, setSinClasificarCollapsed] = useState(() => {
    try {
      return localStorage.getItem('sidebar-sin-clasificar') !== 'open';
    } catch {
      return true;
    }
  });

  const toggleSinClasificar = useCallback(() => {
    setSinClasificarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('sidebar-sin-clasificar', next ? 'closed' : 'open');
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  // DocTooltip for Recientes section
  const {
    tooltip: recentTooltip,
    onMouseEnter: onRecentMouseEnter,
    onMouseLeave: onRecentMouseLeave,
  } = useDocTooltip();

  // ─── expandSection → auto-switch to correct tab ────────────────
  useEffect(() => {
    if (!expandSection) return;

    // 'chats' → switch to Chats tab
    if (expandSection === 'chats') {
      setActiveTab('chats');
      onTabChange?.('chats');
      try {
        localStorage.setItem(TAB_KEY, 'chats');
      } catch {
        /* ignore */
      }
      onExpandSectionHandled?.();
      return;
    }

    // Doc sections → switch to Documentos tab
    if (DOC_SECTIONS.has(expandSection)) {
      setActiveTab('documentos');
      onTabChange?.('documentos');
      try {
        localStorage.setItem(TAB_KEY, 'documentos');
      } catch {
        /* ignore */
      }
    }
    setCollapsedSections((prev) => {
      if (!prev.has(expandSection)) return prev;
      const next = new Set(prev);
      next.delete(expandSection);
      // Accordion: collapse the other aisles
      if (ACCORDION_SECTIONS.has(expandSection)) {
        for (const aisle of ACCORDION_SECTIONS) {
          if (aisle !== expandSection) next.add(aisle);
        }
      }
      return next;
    });
    onExpandSectionHandled?.();
  }, [expandSection, onExpandSectionHandled, onTabChange]);

  // ─── Create conversation shortcut ──────────────────────────────
  const handleCreate = useCallback(async () => {
    await onConversationCreate('Nueva conversación');
  }, [onConversationCreate]);

  // ─── Search focus (via EventBus FOCUS_SIDEBAR_SEARCH) ────────
  const searchInputRef = useRef<HTMLInputElement>(null);
  useEventListener(EventNames.FOCUS_SIDEBAR_SEARCH, () => {
    setTimeout(() => searchInputRef.current?.focus(), 50);
  });

  // ─── Upload private document ─────────────────────────────────
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleUploadClick = useCallback(() => fileInputRef.current?.click(), []);
  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file && onUploadPrivateFile) {
        await onUploadPrivateFile(file);
        privateHook.loadDocuments('');
      }
      e.target.value = '';
    },
    [onUploadPrivateFile, privateHook.loadDocuments]
  );

  // ─── Mobile classes ────────────────────────────────────────────
  const mobileClasses = mobileFullscreen
    ? 'max-md:w-full max-md:flex-1 max-md:border-r-0 max-md:pb-[var(--mobile-nav-total)]'
    : 'max-md:hidden';

  // ─── Render ────────────────────────────────────────────────────
  return (
    <div
      role="navigation"
      aria-label="Barra lateral"
      className={`flex-shrink-0 flex flex-col border-r overflow-hidden transition-all duration-200 ease-out ${
        theme.colors.bg.primary
      } ${theme.colors.border.primary} ${isOpen ? 'w-72' : 'w-0'} ${mobileClasses}`}
      onMouseLeave={onMouseLeave}
      onMouseDown={onInteraction}
    >
      {/* Inner wrapper — fixed min-width prevents content reflow during animation */}
      <div
        className="flex flex-col h-full"
        style={{ minWidth: mobileFullscreen ? undefined : '18rem' }}
      >
        {/* ─── Tab bar ─────────────────────────────────────── */}
        <div className="flex items-center flex-shrink-0" role="tablist" aria-label="Secciones">
          {(['chats', 'documentos'] as SidebarTab[]).map((tab) => (
            <button
              key={tab}
              role="tab"
              aria-selected={activeTab === tab}
              aria-current={activeTab === tab ? 'page' : undefined}
              aria-label={tab === 'chats' ? 'Chats' : 'Documentos'}
              onClick={() => switchTab(tab)}
              className={`flex-1 py-2.5 text-sm text-center transition-colors relative ${
                activeTab === tab
                  ? `font-medium ${theme.colors.text.primary}`
                  : `font-normal ${theme.colors.text.secondary} hover:${theme.colors.text.primary}`
              }`}
            >
              {tab === 'chats' ? 'Chats' : 'Documentos'}
              <div
                className={`absolute bottom-0 left-0 right-0 h-px ${
                  activeTab === tab ? 'bg-nb-accent' : 'bg-white/30'
                }`}
              />
            </button>
          ))}
        </div>

        {/* ─── Search + Quick action ──────────────────────────── */}
        <div className={`px-3 py-1.5 flex-shrink-0 space-y-1`}>
          {/* Search — first in both tabs for consistency */}
          <div className="relative">
            <Search
              className={`absolute left-0 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${theme.colors.text.secondary}`}
            />
            <input
              ref={searchInputRef}
              type="text"
              value={shellSearchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              aria-label={activeTab === 'chats' ? 'Buscar conversaciones' : 'Buscar documentos'}
              placeholder={
                activeTab === 'chats' ? 'Buscar conversaciones...' : 'Buscar documentos...'
              }
              className={`w-full pl-5 pr-3 py-1 text-xs rounded bg-transparent hover:${theme.colors.bg.input} focus:${theme.colors.bg.input} ${theme.colors.text.primary} focus:outline-none focus:ring-1 focus:ring-nb-accent/40 transition-colors`}
            />
          </div>

          {/* Quick action — only for Chats tab */}
          {activeTab === 'chats' && (
            <button
              onClick={handleCreate}
              className={`w-full flex items-center gap-1.5 px-0 py-1.5 min-h-[44px] text-xs ${theme.colors.text.secondary} hover:${theme.colors.text.primary} transition-colors`}
            >
              <Plus className="w-3 h-3" />
              Nueva conversación
            </button>
          )}
        </div>

        {/* ─── Tab content ────────────────────────────────────── */}
        <div className="flex flex-col flex-1 min-h-0">
          {/* ═══ TAB: Chats ═══════════════════════════════════ */}
          {activeTab === 'chats' && (
            <>
              {/* Conversation list — fills available space */}
              <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin">
                <ConversationDrawer
                  conversations={searchedConversations ?? conversations}
                  currentConversation={currentConversation}
                  searchQuery={searchedConversations ? '' : shellSearchTerm}
                  onSelect={onConversationSelect}
                  onCreate={onConversationCreate}
                  onUpdate={onConversationUpdate}
                />
              </div>

              {/* Mini "docs en contexto" footer — only when docs selected */}
              {selectedDocumentIds.length > 0 && (
                <div className={`flex-shrink-0 max-h-[50%] overflow-y-auto scrollbar-thin`}>
                  <div
                    className={`sticky top-0 z-10 px-3 py-1.5 text-xs font-medium ${theme.colors.text.muted} ${theme.colors.bg.primary}`}
                  >
                    Docs en contexto ({selectedDocumentIds.length})
                  </div>
                  <SelectedDocsTab
                    selectedDocuments={selectedDocuments}
                    sources={sources || new Map()}
                    suggestions={suggestions}
                    onDeselect={(id) => {
                      const newIds = selectedDocumentIds.filter((sid) => sid !== id);
                      onDocumentsSelect(newIds);
                    }}
                    documentSelectionMode={documentSelectionMode}
                    userPromptNeeded={userPromptNeeded}
                    isSuggestionsLoading={isSuggestionsLoading}
                  />
                </div>
              )}
            </>
          )}

          {/* ═══ TAB: Documentos ══════════════════════════════ */}
          {activeTab === 'documentos' && (
            <>
              <div className="flex flex-col flex-1 min-h-0">
                {/* ─── Explorar (Sectores / Empresas / Recientes) */}
                <SectionHeader
                  label="Explorar"
                  count={null}
                  icon={Compass}
                  collapsed={collapsedSections.has('explorar')}
                  onToggle={() => toggleCollapse('explorar')}
                  theme={theme}
                />
                {!collapsedSections.has('explorar') && (
                  <div className="flex-shrink-0 max-h-[50%] overflow-y-auto scrollbar-thin">
                    <BibliotecaExplorer
                      onFilterBySector={(sector) => {
                        // Apply sector filter via sharedHook
                        sharedHook.handleFilterClick('sector' as any);
                        sharedHook.handleFilterValueSelect(sector);
                        // Ensure publica section is expanded
                        setCollapsedSections((prev) => {
                          const next = new Set(prev);
                          next.delete('publica');
                          next.add('explorar');
                          // Accordion: collapse other aisles
                          for (const aisle of ACCORDION_SECTIONS) {
                            if (aisle !== 'publica') next.add(aisle);
                          }
                          return next;
                        });
                      }}
                      onFilterByCompany={(company) => {
                        // Apply company filter via sharedHook
                        sharedHook.handleFilterClick('empresa' as any);
                        sharedHook.handleFilterValueSelect(company);
                        // Ensure publica section is expanded
                        setCollapsedSections((prev) => {
                          const next = new Set(prev);
                          next.delete('publica');
                          next.add('explorar');
                          for (const aisle of ACCORDION_SECTIONS) {
                            if (aisle !== 'publica') next.add(aisle);
                          }
                          return next;
                        });
                      }}
                      onDocumentSelect={(docId) => {
                        sharedHook.toggleDocumentSelection(docId);
                      }}
                    />
                  </div>
                )}

                {/* ─── Pública (principal — donde ocurre la mayor interacción) */}
                <SectionHeader
                  label="Biblioteca Pública"
                  count={sharedHook.activeScopeCount}
                  icon={Globe}
                  collapsed={collapsedSections.has('publica')}
                  onToggle={() => toggleCollapse('publica')}
                  theme={theme}
                />
                <div
                  ref={publicaScrollRef}
                  className={`relative overflow-y-auto scrollbar-thin ${
                    collapsedSections.has('publica') ? 'hidden' : 'flex-1 min-h-0'
                  }`}
                >
                  <DocSection
                    documents={classifiedDocs}
                    isLoading={sharedHook.isLoading}
                    searchTerm={shellSearchTerm}
                    sectionScope="shared"
                    selectedDocumentIds={selectedDocumentIds}
                    selectedCount={sharedHook.selectedCount}
                    totalDocCount={sharedHook.activeScopeCount ?? undefined}
                    scrollContainerRef={publicaScrollRef}
                    toggleDocumentSelection={sharedHook.toggleDocumentSelection}
                    selectDocumentRange={sharedHook.selectDocumentRange}
                    handleSelectAll={sharedHook.handleSelectAll}
                    handleDeselectAll={sharedHook.handleDeselectAll}
                    formatDocumentMetadata={sharedHook.formatDocumentMetadata}
                    filters={sharedHook.filters}
                    hasFilters={sharedHook.hasFilters}
                    filterSuggestions={sharedHook.filterSuggestions}
                    isLoadingFilters={sharedHook.isLoadingFilters}
                    handleFilterClick={sharedHook.handleFilterClick}
                    handleFilterValueSelect={sharedHook.handleFilterValueSelect}
                    handleRemoveFilter={sharedHook.handleRemoveFilter}
                    handleClearAllFilters={sharedHook.handleClearAllFilters}
                    loadFilterSuggestions={sharedHook.loadFilterSuggestions}
                    sortOrder={sharedHook.sortOrder}
                    onSortChange={sharedHook.setSortOrder}
                  />

                  {/* ─── Sin clasificar (docs without parsed company/type metadata) */}
                  {unclassifiedDocs.length > 0 && (
                    <>
                      <SectionHeader
                        label="SIN CLASIFICAR"
                        count={unclassifiedDocs.length}
                        icon={FileQuestion}
                        hideIcon
                        collapsed={sinClasificarCollapsed}
                        onToggle={toggleSinClasificar}
                        theme={theme}
                      />
                      {!sinClasificarCollapsed && (
                        <div className="py-1">
                          {unclassifiedDocs.map((doc) => {
                            const isSelected = selectedDocumentIds.includes(doc.id);
                            return (
                              <div
                                key={doc.id}
                                onClick={() => sharedHook.toggleDocumentSelection(doc.id)}
                                role="checkbox"
                                aria-checked={isSelected}
                                tabIndex={0}
                                onKeyDown={(e) => {
                                  if (e.key === ' ' || e.key === 'Enter') {
                                    e.preventDefault();
                                    sharedHook.toggleDocumentSelection(doc.id);
                                  }
                                }}
                                className={`group/doc px-3 py-1.5 cursor-pointer flex items-center gap-2 transition-colors hover:${
                                  theme.colors.bg.secondary
                                } ${
                                  isSelected
                                    ? `${theme.colors.bg.secondary} border-l-2 border-nb-accent`
                                    : ''
                                }`}
                              >
                                <div
                                  className={`flex-shrink-0 transition-opacity ${
                                    isSelected
                                      ? 'text-nb-accent'
                                      : `${theme.colors.text.secondary} opacity-0 group-hover/doc:opacity-100`
                                  }`}
                                >
                                  {isSelected ? (
                                    <div
                                      className={`w-4 h-4 rounded flex items-center justify-center ${theme.colors.bg.primary} border-2 border-nb-accent`}
                                    >
                                      <Check className="w-2.5 h-2.5 text-nb-accent" />
                                    </div>
                                  ) : (
                                    <div
                                      className={`w-4 h-4 rounded border-2 ${theme.colors.border.primary}`}
                                    />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div
                                    className={`text-xs ${theme.colors.text.secondary} truncate`}
                                    title={
                                      doc.original_filename || doc.name || 'Documento sin nombre'
                                    }
                                  >
                                    {doc.original_filename || doc.name || 'Documento sin nombre'}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* ─── Personal (docs adicionales para análisis propio) */}
                <SectionHeader
                  label="Biblioteca Personal"
                  count={privateHook.activeScopeCount}
                  icon={User}
                  collapsed={collapsedSections.has('personal')}
                  onToggle={() => toggleCollapse('personal')}
                  theme={theme}
                  action={
                    onUploadPrivateFile
                      ? { icon: Upload, onClick: handleUploadClick, title: 'Subir documento' }
                      : undefined
                  }
                />
                <div
                  ref={personalScrollRef}
                  className={`relative overflow-y-auto scrollbar-thin ${
                    collapsedSections.has('personal') ? 'hidden' : 'flex-1 min-h-0'
                  }`}
                >
                  <DocSection
                    documents={privateHook.documents}
                    isLoading={privateHook.isLoading}
                    searchTerm={shellSearchTerm}
                    sectionScope="private"
                    selectedDocumentIds={selectedDocumentIds}
                    selectedCount={privateHook.selectedCount}
                    totalDocCount={privateHook.activeScopeCount ?? undefined}
                    scrollContainerRef={personalScrollRef}
                    toggleDocumentSelection={privateHook.toggleDocumentSelection}
                    selectDocumentRange={privateHook.selectDocumentRange}
                    handleSelectAll={privateHook.handleSelectAll}
                    handleDeselectAll={privateHook.handleDeselectAll}
                    formatDocumentMetadata={privateHook.formatDocumentMetadata}
                    filters={privateHook.filters}
                    hasFilters={privateHook.hasFilters}
                    filterSuggestions={privateHook.filterSuggestions}
                    isLoadingFilters={privateHook.isLoadingFilters}
                    handleFilterClick={privateHook.handleFilterClick}
                    handleFilterValueSelect={privateHook.handleFilterValueSelect}
                    handleRemoveFilter={privateHook.handleRemoveFilter}
                    handleClearAllFilters={privateHook.handleClearAllFilters}
                    loadFilterSuggestions={privateHook.loadFilterSuggestions}
                    sortOrder={privateHook.sortOrder}
                    onSortChange={privateHook.setSortOrder}
                  />
                </div>

                {/* ─── Recientes (acceso rápido — accordion aisle) */}
                {recentDocs.length > 0 && (
                  <>
                    <SectionHeader
                      label="Recientes"
                      count={recentDocs.length}
                      icon={Clock}
                      collapsed={collapsedSections.has('recientes')}
                      onToggle={() => toggleCollapse('recientes')}
                      theme={theme}
                    />
                    {!collapsedSections.has('recientes') && (
                      <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin">
                        {recentDocs.map((doc) => {
                          const isSelected = selectedDocumentIds.includes(doc.id);
                          return (
                            <div
                              key={doc.id}
                              onClick={() => sharedHook.toggleDocumentSelection(doc.id)}
                              onMouseEnter={(e) =>
                                onRecentMouseEnter(
                                  {
                                    name: doc.name,
                                    company_name: doc.company,
                                    document_type: doc.type,
                                    document_period: doc.period,
                                  } as DocTooltipDoc,
                                  e
                                )
                              }
                              onMouseLeave={onRecentMouseLeave}
                              className={`group/doc flex items-center gap-2 px-3 py-1.5 cursor-pointer transition-colors hover:${
                                theme.colors.bg.secondary
                              } ${isSelected ? `${theme.colors.bg.secondary}` : ''}`}
                            >
                              <div
                                className={`flex-shrink-0 transition-opacity ${
                                  isSelected
                                    ? 'text-nb-accent'
                                    : `${theme.colors.text.secondary} opacity-0 group-hover/doc:opacity-100`
                                }`}
                              >
                                {isSelected ? (
                                  <div
                                    className={`w-4 h-4 rounded flex items-center justify-center ${theme.colors.bg.primary} border-2 border-nb-accent`}
                                  >
                                    <Check className="w-2.5 h-2.5 text-nb-accent" />
                                  </div>
                                ) : (
                                  <div
                                    className={`w-4 h-4 rounded border-2 ${theme.colors.border.primary}`}
                                  />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div
                                  className={`text-xs font-medium ${theme.colors.text.primary} truncate`}
                                >
                                  {doc.company ||
                                    doc.name?.replace(/_/g, ' ').replace(/\.\w+$/, '') ||
                                    'Documento sin nombre'}
                                </div>
                                {(doc.type || doc.period) && (
                                  <div className="flex items-center gap-1 mt-0.5">
                                    {doc.type && (
                                      <span
                                        className={`text-[10px] font-medium ${
                                          doc.type === 'FECU'
                                            ? 'text-blue-400'
                                            : doc.type === 'ANALISIS'
                                              ? 'text-emerald-400'
                                              : doc.type === 'HE'
                                                ? 'text-amber-400'
                                                : theme.colors.text.secondary
                                        }`}
                                      >
                                        {doc.type}
                                      </span>
                                    )}
                                    {doc.type && doc.period && (
                                      <span className={`text-[10px] ${theme.colors.text.muted}`}>
                                        •
                                      </span>
                                    )}
                                    {doc.period && (
                                      <span
                                        className={`text-[10px] ${theme.colors.text.secondary}`}
                                      >
                                        {doc.period}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                              <div className={`flex-shrink-0 ${theme.colors.text.muted}`}>
                                {doc.scope === 'shared' ? (
                                  <Globe className="w-3.5 h-3.5" />
                                ) : (
                                  <User className="w-3.5 h-3.5" />
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}

                {/* ─── Seleccionados (carrito — resultado de selecciones) */}
                <SectionHeader
                  label="Seleccionados"
                  count={selectedDocumentIds.length}
                  showZeroCount
                  icon={Check}
                  collapsed={collapsedSections.has('seleccionados')}
                  onToggle={() => toggleCollapse('seleccionados')}
                  theme={theme}
                />
                {!collapsedSections.has('seleccionados') && selectedDocumentIds.length > 0 && (
                  <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin">
                    <SelectedDocsTab
                      selectedDocuments={selectedDocuments}
                      sources={sources || new Map()}
                      suggestions={suggestions}
                      onDeselect={(id) => {
                        const newIds = selectedDocumentIds.filter((sid) => sid !== id);
                        onDocumentsSelect(newIds);
                      }}
                      documentSelectionMode={documentSelectionMode}
                      userPromptNeeded={userPromptNeeded}
                      isSuggestionsLoading={isSuggestionsLoading}
                    />
                  </div>
                )}

                {/* Recientes tooltip (portal) */}
                {recentTooltip && (
                  <DocTooltip doc={recentTooltip.doc} anchor={recentTooltip.rect} />
                )}
              </div>

              {/* "Gestionar biblioteca →" footer */}
              {onToggleDrive && (
                <div className={`flex-shrink-0 border-t ${theme.colors.border.primary} px-3 py-2`}>
                  <button
                    onClick={() => {
                      onToggleDrive();
                      onClose();
                    }}
                    aria-label="Gestionar biblioteca"
                    className={`flex items-center gap-2 text-xs text-nb-accent hover:underline min-h-[44px]`}
                  >
                    <HardDrive className="w-3.5 h-3.5" />
                    Gestionar biblioteca →
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Hidden file input for Personal section upload */}
      {onUploadPrivateFile && (
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf"
          className="hidden"
          onChange={handleFileChange}
        />
      )}
    </div>
  );
};

export default UnifiedSidebar;
